import {
  Callout,
  Figure,
  H2,
  H3,
  InfoTable,
  LI,
  P,
  Table,
  UL,
} from "../../components/blog/BlogTypography";

import forumPostOverview from "../../assets/blog/pappyjoe-breach/forum-post-overview.png";
import forumProfilePanel from "../../assets/blog/pappyjoe-breach/forum-profile-panel.png";
import claimSummary from "../../assets/blog/pappyjoe-breach/claim-summary.png";
import kazuLeakSiteListing from "../../assets/blog/pappyjoe-breach/kazu-leak-site-listing.png";

const ASSESSMENT_ROWS = [
  ["Observed claim", "Extortion post alleging theft of PappyJoe healthcare-management data"],
  ["Threat actor", "Kazu / Kazu-branded forum and leak-site presence"],
  ["Claim date", "July 18, 2026"],
  ["Claimed volume", "413 GB"],
  ["Claimed records", "6,873,180 patient-related records and 531,673 files"],
  ["Claimed ransom", "US$250,000"],
  ["Claimed deadline", "August 5, 2026"],
  ["Confidence", "High confidence that the post exists; low confidence in the unverified data volume and scope"],
  ["Potential severity", "Critical if authentic, because healthcare data is sensitive and may affect multiple organisations"],
];

const TIMELINE_HEADERS = ["Date", "Reported activity", "Assessment / source context"];

const TIMELINE_ROWS = [
  [
    "June–August 2025",
    "Early underground activity associated with the name Kazu is recorded by some trackers. CYFIRMA reported an actor named Kazu offering administrator access to a Saudi online store in August.",
    "Possible precursor activity; public sources do not conclusively prove that every use of the Kazu handle belongs to the later extortion group.",
  ],
  [
    "September 2025",
    "Bitdefender identifies Kazu as an active ransomware/extortion group and later reports claims involving government, military and healthcare organisations.",
    "Useful marker for the group's emergence as a recognisable extortion operation.",
  ],
  [
    "7–11 November 2025",
    "Kazu claims theft of approximately 353 GB and more than 1.2 million files from US healthcare-technology provider Doctor Alliance, demanding US$200,000.",
    "The claim was publicly reported; Doctor Alliance said it was investigating.",
  ],
  [
    "9–15 December 2025",
    "Red Piranha profiles Kazu in a weekly threat-intelligence report, reflecting growing visibility in the ransomware ecosystem.",
    "Supports the assessment that Kazu had become an established monitored actor by late 2025.",
  ],
  [
    "30 December 2025 – January 2026",
    "A breach of New Zealand patient portal Manage My Health is identified. Kazu claims 428,337 files totalling 108 GB and demands US$60,000. New Zealand's Ministry of Health later confirms unauthorised access to sensitive clinical and personal information affecting more than 99,000 individuals.",
    "The strongest publicly corroborated incident associated with the Kazu name, since the underlying breach was confirmed by government review — although attribution still rests on the actor's claim.",
  ],
  [
    "12 January 2026",
    "Security reporting records a Kazu claim against US veterinary-software provider MyVete.",
    "Secondary reporting only; treat victim scope and technical details as unverified.",
  ],
  [
    "16 May 2026",
    "Kazu publishes a “We're Back” notice saying the group had been offline for several months because of internal issues.",
    "Based on the supplied screenshot. The start date and cause of the interruption are not independently confirmed.",
  ],
  [
    "18 July 2026",
    "The supplied forum screenshot shows Kazu claiming 413 GB of PappyJoe data, 6,873,180 patient-related records and 531,673 files, with a US$250,000 demand and 5 August deadline. The leak site also lists several healthcare and medical-technology organisations.",
    "The clustering of healthcare targets is consistent with Kazu's previously reported focus.",
  ],
];

export default function PappyJoeBreachClaim() {
  return (
    <>
      <Callout>
        Analysis based on an underground forum screenshot, a Kazu-branded leak site, public
        reporting, and CyberMon DRI assessment. Claims should be treated as alleged unless
        independently verified. Sensitive contact and sample details in the evidence screenshots
        have been intentionally redacted for publication.
      </Callout>

      <Figure
        src={forumPostOverview}
        alt="Safe overview of the underground forum post titled 'India - PappyJoe: Healthcare Management System - 413GB', posted by user Kazu, showing a PappyJoe clinic management software banner, organization overview, dump date, total size, record counts, ransom amount, and deadline, with a redacted sensitive contact and sample block at the bottom."
        caption="Figure 1: Safe overview of the underground forum post naming PappyJoe. Sensitive contact and sample details have been intentionally redacted for publication."
      />

      <H2>Executive Summary</H2>
      <P>
        On July 18, 2026, an account using the name "Kazu" posted on an underground forum claiming
        to possess data allegedly linked to PappyJoe, an India-based healthcare technology
        provider. The listing claims that 413 GB of data was obtained, including 6,873,180
        patient-related records and 531,673 files. The actor demanded US$250,000 and listed August
        5, 2026 as the deadline before the data would allegedly be sold.
      </P>
      <P>
        The same organisation was also displayed on a Kazu-branded extortion site alongside
        several healthcare, telemedicine, hospital and medical-technology entities. This sector
        concentration is important because healthcare-management platforms can hold identity
        information, appointment histories, prescriptions, clinical notes, billing records and
        operational data across multiple customers.
      </P>
      <P>
        At the time of this assessment, CyberMon did not identify an authoritative public
        confirmation from PappyJoe, a regulator or law-enforcement body validating the actor's
        stated data volume, record count or intrusion scope.
      </P>

      <H3>At-a-Glance Assessment</H3>
      <InfoTable rows={ASSESSMENT_ROWS} />

      <H2>What the Underground Forum Screenshot Shows</H2>
      <P>
        The forum page is titled "India - PappyJoe: Healthcare Management System - 413GB." The
        post appears to be authored by an account named Kazu and includes a company overview,
        claimed dump year, alleged record and file counts, a ransom demand, a deadline and contact
        channels. The account profile shown in the screenshot had joined the forum in June 2026
        and displayed a small number of posts and threads at the time of capture.
      </P>

      <Figure
        src={forumProfilePanel}
        alt="Forum profile panel for user Kazu, showing an avatar image of a hooded figure, an 'MVP User' tag, a white MVP badge, and stats: 9 posts, 9 threads, joined June 2026, reputation 110, with Sellix, Discord and Telegram contact icons."
        caption="Figure 2: Forum profile and post metadata associated with the Kazu account — an MVP-tagged account with 9 posts, 9 threads, joined June 2026, and a reputation score of 110."
      />

      <Figure
        src={claimSummary}
        alt="Claim summary text block reading: Organization PappyJoe: Healthcare Management System (pappyjoe.com); Overview - PappyJoe is an India-based healthcare technology company that provides a cloud-based practice management platform for clinics, hospitals, and healthcare professionals; Dump Date 2026; Total Size 413 GB; Record: 6,873,180 patients pii plus 531,673 files; Ransom 250,000 dollars, if they don't pay the ransom we will edit the post and sell it; Deadline 05/08/2026."
        caption="Figure 3: Claim summary from the forum post, excluding the sensitive contact and sample section."
      />

      <H2>Claim Details and Material Referenced in the Post</H2>
      <P>
        The post presents a series of numerical claims and references a sample dataset that
        contains sensitive personal and health-related information. The alleged data includes
        patient names, identification numbers, Aadhaar-related information, dates of birth,
        contact details, residential addresses, admission and referral information, laboratory
        reports, prescriptions, dental images, radiographs and ultrasound records.
      </P>
      <P>
        The records shown in the sample appear to reference individuals from multiple Indian
        states, including Puducherry, Tamil Nadu, Kerala, Karnataka, Andhra Pradesh, Uttar Pradesh,
        Punjab and Bihar. Separate pathology-related documents also appear to reference Zambia.
        These observations are based solely on the information shared by the threat actor.
      </P>
      <P>
        If the data is authentic and relates to real individuals, the combination of identity,
        contact, government-issued identification and medical information could create
        significant and long-term privacy and fraud risks. Potential concerns may include identity
        theft, misuse of Aadhaar-related information, targeted phishing, healthcare impersonation
        and unauthorised disclosure of medical information.
      </P>
      <P>
        The apparent presence of identifiable clinical images and image metadata may also increase
        re-identification and location-privacy risks. In such cases, removing or changing
        filenames alone may not be sufficient to anonymise the underlying content.
      </P>

      <H2>About the "Kazu" Group</H2>
      <P>
        Kazu is an emerging, financially motivated cyber-extortion operation that became visible
        in 2025. Some researchers describe it as a ransomware group using double extortion, while
        others classify it more cautiously as a data broker or direct-extortion actor. This
        distinction matters because public evidence of data theft and leak-site pressure is
        stronger than public evidence of a consistently deployed Kazu encryption payload.
      </P>
      <P>
        Bitdefender reported that the group had been active since September 2025 and had already
        claimed organisations in government, military and healthcare, with a concentration of
        victims in Southeast Asia, the Middle East and South America. WatchGuard lists Kazu as
        active and records both direct- and double-extortion activity, but categorises its
        ransomware type as "Data Broker." Taken together, the most defensible assessment is that
        Kazu is primarily a financially motivated data-extortion actor that uses public claims,
        deadlines, samples and threatened publication or sale to pressure victims.
      </P>
      <P>
        The operators' real identities, location, affiliate structure and relationship to any
        earlier ransomware group remain unconfirmed.
      </P>

      <H3>Observed Operating Pattern</H3>
      <UL>
        <LI>
          Targets organisations that hold high-value or highly sensitive data, with repeated
          visibility in healthcare and public-sector reporting.
        </LI>
        <LI>
          Publishes structured victim posts containing an alleged data volume, record count,
          ransom amount and payment deadline.
        </LI>
        <LI>
          Uses underground forums, a branded leak site and messaging channels to amplify pressure
          and distribute alleged proof.
        </LI>
        <LI>
          Threatens public release or resale of stolen information, indicating that data theft
          itself may be the core monetisation method.
        </LI>
        <LI>
          May temporarily remove or restore infrastructure; the group's own 16 May 2026 notice
          said it had returned after being offline for several months, but did not explain the
          cause.
        </LI>
      </UL>

      <H2>Timeline of Recent Kazu Activity</H2>
      <P>
        The following timeline combines established public reporting with the screenshots
        supplied for this assessment. Entries involving criminal claims describe allegations, not
        independently verified breach scope.
      </P>
      <Table headers={TIMELINE_HEADERS} rows={TIMELINE_ROWS} />
      <Callout>
        <strong>Takeaway:</strong> Kazu's recent public footprint shows repeated use of
        healthcare-sector extortion claims, escalating ransom demands, deadline-driven leak
        pressure and multi-channel publication. The timeline does not establish a single shared
        intrusion method, but it does indicate a sustained preference for organisations holding
        large volumes of sensitive clinical or operational data.
      </Callout>

      <H2>Why PappyJoe Is a High-Impact Target</H2>
      <P>
        PappyJoe publicly describes its platform as clinic, dental and hospital management
        software used for patient records, appointments, prescriptions, billing, teleconsultation
        and related healthcare workflows. Its website states that the platform supports doctors,
        clinics and hospitals and includes cloud-based access to electronic medical records and
        reports.
      </P>
      <P>
        This means a confirmed compromise could extend beyond a single corporate network.
        Depending on how the affected environment was structured, the exposed data could relate to
        PappyJoe's own systems, a shared software-as-a-service environment, customer-specific
        deployments, support tools, backups, integrations or an external service provider.
      </P>

      <H3>Potential Data Categories at Risk</H3>
      <UL>
        <LI>Patient identity and contact information, including names, phone numbers, email addresses and addresses.</LI>
        <LI>Electronic medical records, consultation notes, diagnoses, prescriptions and medical histories.</LI>
        <LI>Appointment histories, doctor-patient communications and teleconsultation records.</LI>
        <LI>Laboratory reports, imaging references, attachments and clinical documents.</LI>
        <LI>Billing, invoice, payment and insurance-related information.</LI>
        <LI>Healthcare-provider, employee, administrator and support-account information.</LI>
        <LI>Application configuration files, database exports, backups and internal operational documents.</LI>
      </UL>
      <P>
        These categories are based on the functions publicly associated with healthcare-management
        platforms and PappyJoe's advertised capabilities. They are plausible risk scenarios, not
        confirmation that each data type was stolen.
      </P>

      <Figure
        src={kazuLeakSiteListing}
        alt="Kazu-branded extortion 'Ransoms' leak site listing multiple healthcare-sector organisations with countdown timers, including ConsultorioMovil (Telemedicine, Mexico), Meducar (Telemedicine), Centro Medico Especializado OSI (Healthcare Solutions), Dr Akbar Niazi Teaching Hospital, PawlyClinic (Digital Veterinary Care), Yocale (Canada Appointment Management), Mobilemed (Brazil Cloud PACS Platform), Instituto Ferrero de Neurologia y Sueno, and PappyJoe (Healthcare Management System)."
        caption="Figure 4: Kazu-branded extortion page displaying PappyJoe among multiple healthcare-sector organisations."
      />

      <H2>Why This Matters Beyond One Healthcare Vendor</H2>

      <H3>1. Healthcare Data Has Long-Term Value</H3>
      <P>
        Passwords can be reset, but medical histories, diagnoses and treatment records cannot be
        changed. Stolen health information may support identity theft, targeted phishing,
        insurance fraud, blackmail and highly personalised social-engineering campaigns.
      </P>

      <H3>2. A Platform Breach Can Become a Multi-Organisation Incident</H3>
      <P>
        Healthcare software providers frequently serve many independent clinics and hospitals. If
        a shared platform or central support environment is compromised, the incident can create
        downstream exposure across multiple customers, jurisdictions and contractual
        relationships.
      </P>

      <H3>3. Extortion Does Not Require Encryption</H3>
      <P>
        Modern extortion actors often steal data and threaten publication or sale without
        deploying file-encrypting ransomware. An organisation may therefore remain operational
        while still facing severe privacy, regulatory and reputational consequences.
      </P>

      <H3>4. Stolen Patient Data Enables Secondary Attacks</H3>
      <P>
        An exposed dataset can be combined with leaked credentials, public social-media
        information and impersonation domains to create convincing messages aimed at patients,
        doctors, finance teams and support staff.
      </P>

      <H3>5. The Claim May Involve a Third Party or Historical Dataset</H3>
      <P>
        A criminal listing does not prove that a company's current production platform was
        breached. The data could have originated from a customer deployment, former vendor,
        backup, development environment, compromised endpoint or older dataset. Incident scoping
        must therefore test multiple hypotheses rather than assuming a single intrusion path.
      </P>

      <H2>Signals Security Teams Should Watch</H2>
      <UL>
        <LI>
          Underground forum or leak-site posts mentioning the organisation, product names,
          subsidiaries, customers or healthcare vertical.
        </LI>
        <LI>
          Claims using terms such as patient database, EMR, clinic software, hospital system,
          appointment records, prescriptions or medical files.
        </LI>
        <LI>Countdown pages, ransom deadlines and claims that data will be sold if payment is not made.</LI>
        <LI>Newly registered domains or social-media profiles impersonating the affected company after a breach claim.</LI>
        <LI>Large database exports, archive creation, unusual queries or high-volume outbound data transfers.</LI>
        <LI>
          Authentication anomalies involving administrators, support accounts, API tokens, cloud
          consoles or remote-access services.
        </LI>
      </UL>

      <H2>Recommended Actions for PappyJoe and Potentially Affected Customers</H2>

      <H3>Incident Validation and Evidence Preservation</H3>
      <UL>
        <LI>Activate the incident-response, legal, privacy, executive and communications teams.</LI>
        <LI>
          Preserve identity, endpoint, cloud, database, application, VPN, API gateway, email and
          support-system logs.
        </LI>
        <LI>
          Validate any lawfully obtained sample against controlled internal records using minimal
          exposure and documented chain of custody.
        </LI>
        <LI>
          Determine whether the alleged data relates to a central SaaS environment, a customer
          deployment, a backup, a third party or historical information.
        </LI>
      </UL>

      <H3>Identity and Access Review</H3>
      <UL>
        <LI>
          Review privileged accounts, newly created users, disabled or dormant accounts, MFA
          changes and suspicious OAuth grants.
        </LI>
        <LI>
          Revoke active sessions and rotate administrative passwords, API keys, database
          credentials, service-account secrets and support tokens where exposure is suspected.
        </LI>
        <LI>Investigate anomalous access to customer tenants, support consoles, backups and cloud-storage services.</LI>
      </UL>

      <H3>Data Exfiltration Hunting</H3>
      <UL>
        <LI>
          Search for large database exports, compressed archives, staging directories and unusual
          command-line activity.
        </LI>
        <LI>
          Review outbound traffic, cloud object downloads, database-query patterns and large
          transfers to unfamiliar destinations.
        </LI>
        <LI>
          Examine endpoint and server telemetry for remote-access tools, credential dumping,
          tunnelling and data-compression utilities.
        </LI>
      </UL>

      <H3>Customer and Regulatory Coordination</H3>
      <UL>
        <LI>
          Identify potentially affected clinics, hospitals and regions based on verified scope
          rather than the actor's headline numbers.
        </LI>
        <LI>Prepare clear customer communications that separate confirmed facts from criminal allegations.</LI>
        <LI>
          Assess reporting obligations under applicable contracts, privacy laws and sector
          requirements. CERT-In directions require covered incidents to be reported within six
          hours of noticing them.
        </LI>
      </UL>

      <H2>How CyberMon Helps Organisations Stay Protected</H2>
      <P>
        CyberMon combines Digital Risk Intelligence (DRI) and Attack Surface Intelligence (ASI) to
        help security teams detect external threat signals, validate emerging claims and reduce
        the weaknesses that attackers can exploit.
      </P>

      <H3>CyberMon Digital Risk Intelligence</H3>
      <UL>
        <LI>
          Underground forum and ransomware monitoring: identifies victim listings, data-sale
          posts, actor mentions and extortion activity involving company names, products,
          subsidiaries and customers.
        </LI>
        <LI>
          Credential and data-leak intelligence: detects exposed credentials, sensitive-data
          references and leak indicators that may require password resets, token revocation or
          forensic review.
        </LI>
        <LI>
          Phishing and impersonation monitoring: tracks lookalike domains, fraudulent websites,
          fake support profiles and brand impersonation that may appear before or after a public
          breach claim.
        </LI>
        <LI>
          Campaign correlation: connects actor handles, leak sites, domains, Telegram channels and
          repeated targeting patterns to help analysts understand whether a claim is isolated or
          part of a broader campaign.
        </LI>
        <LI>
          Evidence-led alerting: provides screenshots, timestamps, source context and structured
          findings so teams can triage claims quickly without relying only on social-media
          reports.
        </LI>
      </UL>

      <H3>CyberMon Attack Surface Intelligence</H3>
      <UL>
        <LI>Continuously discovers internet-facing domains, subdomains, IP addresses, services and technologies.</LI>
        <LI>
          Identifies exposed ports, vulnerable services, weak TLS configurations, web-security
          issues and misconfigured administrative interfaces.
        </LI>
        <LI>Tracks newly exposed assets and changes that may create an initial-access opportunity.</LI>
        <LI>Prioritises remediation based on reachability, severity and business impact.</LI>
        <LI>Provides executive-ready reporting that connects technical exposure with practical risk and ownership.</LI>
      </UL>
      <P>
        Together, CyberMon DRI and ASI help organisations move from reactive breach awareness to
        continuous external visibility. DRI can identify when an organisation is being discussed,
        impersonated or extorted, while ASI helps reduce the internet-facing weaknesses that may
        enable an intrusion.
      </P>

      <H2>Conclusion</H2>
      <P>
        The Kazu post naming PappyJoe should currently be treated as a serious but unverified
        cybercriminal claim. The potential impact is nevertheless significant because
        healthcare-management platforms can aggregate sensitive patient, clinical, financial and
        operational information across multiple organisations. The appropriate response is to
        investigate quickly, preserve evidence, validate scope and communicate carefully without
        repeating attacker claims as confirmed facts.
      </P>
      <P>
        For healthcare providers and technology vendors, the broader lesson is that external
        threat monitoring and attack-surface visibility must operate together. Organisations need
        to know both when criminals are discussing their data and where externally exposed
        weaknesses may give attackers a path into the environment.
      </P>
    </>
  );
}
